/**
 * The Google button, when there is one.
 *
 * Three things have to happen before anybody can press it: the server has to
 * say Google sign-in is configured and hand over the public client id, Google's
 * own script has to load, and that script has to draw the button. Any of the
 * three can fail — a studio with no Google set up, a blocked script, no network
 * — and in every case the right answer is the same: no button, no error, and
 * the password field still there. Nobody is stuck because the second way in did
 * not appear.
 *
 * ## Why Google draws the button
 *
 * `renderButton` produces Google's own button inside an iframe, which cannot be
 * styled to match anything here. That is the cost, and it is worth paying:
 * Google's branding terms require their mark on it, the button is the thing
 * people recognise, and the alternative — our own button driving the OAuth
 * code flow — means a client secret, a redirect route and a callback to get
 * wrong, in exchange for a rounder corner.
 *
 * ## Why the script is loaded here rather than in index.html
 *
 * A tag in the HTML loads Google's script for every page of the platform,
 * including the client-facing share page, which has no accounts in it at all.
 * This loads it on the one screen that uses it and only when that screen has
 * been told there is something to use it for.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'

const SCRIPT = 'https://accounts.google.com/gsi/client'

/** Only the shape actually used. Google's own typings are a separate package. */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: {
        client_id: string
        callback: (response: { credential?: string }) => void
        cancel_on_tap_outside?: boolean
        auto_select?: boolean
      }): void
      renderButton(
        parent: HTMLElement,
        options: {
          type?: 'standard' | 'icon'
          theme?: 'outline' | 'filled_blue' | 'filled_black'
          size?: 'small' | 'medium' | 'large'
          text?: 'signin_with' | 'continue_with'
          shape?: 'rectangular' | 'pill'
          logo_alignment?: 'left' | 'center'
          width?: number
        }
      ): void
      disableAutoSelect(): void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

/**
 * Load Google's script once per page, however many callers ask.
 *
 * Kept outside the hook because React will mount this screen more than once —
 * StrictMode does it deliberately — and two copies of the script means two
 * copies of the button.
 */
let loading: Promise<GoogleIdentityServices> | null = null

function gis(): Promise<GoogleIdentityServices> {
  if (window.google !== undefined) return Promise.resolve(window.google)
  if (loading !== null) return loading

  loading = new Promise<GoogleIdentityServices>((resolve, reject) => {
    const tag = document.createElement('script')
    tag.src = SCRIPT
    tag.async = true
    tag.defer = true
    tag.onload = () => {
      if (window.google === undefined) {
        reject(new Error('Google’s script loaded but defined nothing.'))
        return
      }
      resolve(window.google)
    }
    tag.onerror = () => {
      // So a later attempt can try again rather than being handed a
      // permanently rejected promise from the first blocked load.
      loading = null
      reject(new Error('Could not load Google’s script.'))
    }
    document.head.appendChild(tag)
  })

  return loading
}

export interface GoogleSignIn {
  /** Put this on the element Google should draw its button into. */
  ref: (node: HTMLDivElement | null) => void
  /**
   * This deployment has Google configured, so keep `ref`'s element in the
   * document — collapsed if you like, but present. Google draws into it, and
   * an element that only appears once the button exists is an element the
   * button can never be drawn into. That was the first version of this.
   */
  configured: boolean
  /** Google has actually drawn the button. Now it is worth taking up space. */
  offered: boolean
  /** Signing in right now. */
  busy: boolean
  /** What went wrong, in words for the person reading, or null. */
  error: string | null
}

/**
 * @param onSignedIn called after the server has accepted the token and set the
 *   session cookie. Usually `refresh()` followed by a navigate.
 */
export function useGoogleSignIn(onSignedIn: () => Promise<void> | void): GoogleSignIn {
  const [clientId, setClientId] = useState<string | null>(null)
  const [drawn, setDrawn] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // State rather than a ref, because the effect below has to run again when
  // the element arrives — and a ref changing is exactly the thing React does
  // not notice. This was a ref first, and the button never drew.
  const [host, setHost] = useState<HTMLDivElement | null>(null)

  // In a ref so that the callback handed to Google never goes stale: it is
  // registered once, and Google keeps whichever function it was given.
  const done = useRef(onSignedIn)
  done.current = onSignedIn

  /* -- is there a button to draw? ------------------------------------- */

  useEffect(() => {
    let live = true
    void api<{ google: { client_id: string } | null }>('/session/options', { unversioned: true })
      .then((options) => {
        if (live && options.google !== null) setClientId(options.google.client_id)
      })
      .catch(() => {
        // Deliberately silent. A studio with no Google configured and a server
        // that could not be reached look the same from here, and in both cases
        // the useful thing on screen is the password field.
      })
    return () => {
      live = false
    }
  }, [])

  /* -- the exchange --------------------------------------------------- */

  const send = useCallback(async (credential: string) => {
    setBusy(true)
    setError(null)
    try {
      await api('/session/google', { method: 'POST', body: { credential }, unversioned: true })
      await done.current()
    } catch (err) {
      setError(
        err instanceof Error && err.message !== ''
          ? err.message
          : 'Could not sign in with Google. Try your password.',
      )
      // So a second attempt prompts again rather than silently reusing the
      // account that was just refused.
      window.google?.accounts.id.disableAutoSelect()
    } finally {
      setBusy(false)
    }
  }, [])

  /* -- drawing it ------------------------------------------------------ */

  useEffect(() => {
    if (clientId === null || host === null) return
    let live = true

    void gis()
      .then((google) => {
        if (!live) return
        google.accounts.id.initialize({
          client_id: clientId,
          callback: ({ credential }) => {
            if (typeof credential === 'string' && credential !== '') void send(credential)
          },
          // No One Tap prompt. This screen is already asking somebody to sign
          // in; a second, different prompt floating over it is the thing people
          // dismiss without reading.
          auto_select: false,
          cancel_on_tap_outside: true,
        })
        google.accounts.id.renderButton(host, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'center',
          width: 320,
        })
        setDrawn(true)
      })
      .catch(() => {
        // Blocked, offline, or an extension in the way. No button, no error:
        // the password field below it works and saying "Google's script did
        // not load" helps nobody standing on a shoot.
      })

    return () => {
      live = false
    }
  }, [clientId, host, send])

  const ref = useCallback((node: HTMLDivElement | null) => {
    setHost(node)
  }, [])

  return { ref, configured: clientId !== null, offered: drawn, busy, error }
}
